import { useState, useCallback, useMemo } from "react";
import { SupabaseNotification } from "@/types/notifications";

export const useNotificationBulkSelection = (notifications: SupabaseNotification[]) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const selectedCount = selectedIds.size;
  const isAllSelected = notifications.length > 0 && selectedIds.size === notifications.length;
  const isPartiallySelected = selectedIds.size > 0 && selectedIds.size < notifications.length;

  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(notifications.map(n => n.id)));
    }
  }, [isAllSelected, notifications]);

  const toggleSelectItem = useCallback((id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback((id: string) => {
    return selectedIds.has(id);
  }, [selectedIds]);

  const selectedNotifications = useMemo(() => {
    return notifications.filter(n => selectedIds.has(n.id));
  }, [notifications, selectedIds]);

  return {
    selectedIds,
    selectedCount,
    selectedNotifications,
    isAllSelected,
    isPartiallySelected,
    toggleSelectAll,
    toggleSelectItem,
    clearSelection,
    isSelected
  };
};